import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Card, CardContent, TextField, Select, MenuItem, FormControl, InputLabel, Typography, Box, RadioGroup, FormControlLabel, Radio, Stack, Divider, } from '@mui/material';
import { useTheme } from '@mui/material/styles';
const LENGTH_FACTORS_M = {
    mm: 0.001,
    cm: 0.01,
    m: 1,
    in: 0.0254,
    ft: 0.3048,
};
const AREA_FACTORS_M2 = {
    'mm²': 1e-6,
    'cm²': 1e-4,
    'm²': 1,
    'in²': 0.00064516,
    'ft²': 0.09290304,
};
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
function formatNumber(n, digits = 4) {
    return Number.isFinite(n) ? n.toFixed(digits) : '';
}
const AreaOfCircle = () => {
    const theme = useTheme();
    const [measureType, setMeasureType] = useState('radius');
    const [measureValue, setMeasureValue] = useState('');
    const [inputUnit, setInputUnit] = useState('m');
    const [outputUnit, setOutputUnit] = useState('m²');
    const parsedValue = useMemo(() => {
        const v = parseFloat(measureValue);
        return Number.isFinite(v) ? v : NaN;
    }, [measureValue]);
    const isValid = useMemo(() => {
        return Number.isFinite(parsedValue) && parsedValue >= 0;
    }, [parsedValue]);
    const { radius_m, area_m2: _area_m2, area_out, radius_display, } = useMemo(() => {
        if (!isValid) {
            return {
                radius_m: NaN,
                area_m2: NaN,
                area_out: NaN,
                radius_display: '',
            };
        }
        const length_m = parsedValue * LENGTH_FACTORS_M[inputUnit];
        const r_m = measureType === 'radius' ? length_m : length_m / 2;
        const a_m2 = Math.PI * r_m * r_m;
        const a_out = a_m2 / AREA_FACTORS_M2[outputUnit];
        // radius displayed back in input unit
        const r_in_unit = r_m / LENGTH_FACTORS_M[inputUnit];
        const r_disp = formatNumber(r_in_unit);
        return {
            radius_m: r_m,
            area_m2: a_m2,
            area_out: a_out,
            radius_display: r_disp,
        };
    }, [isValid, parsedValue, inputUnit, outputUnit, measureType]);
    const ariaLabel = useMemo(() => {
        if (!isValid)
            return 'Circle area visualization: invalid input';
        return `Circle area visualization: radius ${radius_display} ${inputUnit}, area ${formatNumber(area_out)} ${outputUnit}`;
    }, [isValid, radius_display, inputUnit, area_out, outputUnit]);
    // SVG scaling for radius (pixels) based on magnitude of radius in meters
    const r_px = useMemo(() => {
        if (!isValid)
            return 24;
        const r = Math.max(radius_m, 1e-9);
        const m = Math.log10(r);
        return clamp(80 + 20 * m, 24, 100);
    }, [isValid, radius_m]);
    return (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", gutterBottom: true, children: "Area of a Circle" }), _jsxs(Stack, { spacing: 2, sx: { mt: 2 }, children: [_jsx(FormControl, { component: "fieldset", children: _jsxs(RadioGroup, { row: true, value: measureType, onChange: (e) => setMeasureType(e.target.value), children: [_jsx(FormControlLabel, { value: "radius", control: _jsx(Radio, {}), label: "Radius" }), _jsx(FormControlLabel, { value: "diameter", control: _jsx(Radio, {}), label: "Diameter" })] }) }), _jsxs(Box, { sx: { display: 'flex', gap: 2 }, children: [_jsx(TextField, { label: measureType === 'radius' ? 'Radius' : 'Diameter', type: "number", inputProps: { min: 0, step: 'any' }, value: measureValue, onChange: (e) => setMeasureValue(e.target.value), fullWidth: true }), _jsxs(FormControl, { sx: { minWidth: 120 }, children: [_jsx(InputLabel, { children: "Unit" }), _jsxs(Select, { label: "Unit", value: inputUnit, onChange: (e) => setInputUnit(e.target.value), children: [_jsx(MenuItem, { value: "mm", children: "mm" }), _jsx(MenuItem, { value: "cm", children: "cm" }), _jsx(MenuItem, { value: "m", children: "m" }), _jsx(MenuItem, { value: "in", children: "in" }), _jsx(MenuItem, { value: "ft", children: "ft" })] })] })] }), _jsxs(FormControl, { sx: { minWidth: 160 }, children: [_jsx(InputLabel, { children: "Output Unit" }), _jsxs(Select, { label: "Output Unit", value: outputUnit, onChange: (e) => setOutputUnit(e.target.value), children: [_jsx(MenuItem, { value: "mm\u00B2", children: "mm\u00B2" }), _jsx(MenuItem, { value: "cm\u00B2", children: "cm\u00B2" }), _jsx(MenuItem, { value: "m\u00B2", children: "m\u00B2" }), _jsx(MenuItem, { value: "in\u00B2", children: "in\u00B2" }), _jsx(MenuItem, { value: "ft\u00B2", children: "ft\u00B2" })] })] }), !isValid && measureValue !== '' && (_jsx(Typography, { color: "error", children: "Value must be a non-negative number." })), isValid && (_jsxs(Stack, { spacing: 2, children: [_jsx(Card, { sx: { bgcolor: 'primary.main', color: 'primary.contrastText' }, children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", children: "Result" }), _jsx(Typography, { variant: "h4", children: formatNumber(area_out) }), _jsx(Typography, { variant: "body2", children: outputUnit }), _jsx(Divider, { sx: { my: 1, opacity: 0.4 } }), _jsxs(Typography, { variant: "body2", children: ["Computed from ", measureType, " = ", radius_display, " ", inputUnit, measureType === 'diameter' ? ' (radius derived from diameter)' : ''] })] }) }), _jsx(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }, children: _jsx("svg", { role: "img", "aria-label": ariaLabel, viewBox: "0 0 240 240", width: "100%", height: "240", children: _jsxs("g", { transform: "translate(120,120)", style: { color: theme.palette.primary.main }, children: [_jsx("circle", { cx: 0, cy: 0, r: r_px, fill: theme.palette.primary.main, fillOpacity: 0.2, stroke: theme.palette.primary.main, strokeWidth: 2 }), _jsx("line", { x1: 0, y1: 0, x2: r_px, y2: 0, stroke: theme.palette.primary.main, strokeWidth: 2 }), _jsxs("text", { x: 0, y: 4, textAnchor: "middle", fontSize: 14, fill: theme.palette.text.primary, children: ["A = ", formatNumber(area_out), " ", outputUnit] }), _jsxs("text", { x: r_px, y: -8, textAnchor: "end", fontSize: 12, fill: theme.palette.text.primary, children: ["r = ", radius_display, " ", inputUnit] })] }) }) })] }))] })] }) }));
};
export default AreaOfCircle;
